import React, { useCallback, useState } from 'react';

import {
    getMeta,
    getPropertyKeysBySection,
    sections,
} from '../../../lib/playerPreferenceMeta';
import {
    THEME_GROUPS,
    THEME_KEYS,
    resolvedDefault,
    pairTabOutline,
    extractTheme,
    presetValues,
    hasTheme,
    matchPreset,
    modalSuggestion,
    tabSuggestion,
    inertWarning,
    surfaceVariance,
    sizeCaution,
} from '../../../lib/themeTokens';
import InfoTip from './InfoTip';
import {
    auditContrast,
    findingsByKey,
    tintDirectionWarning,
    severityLabel,
    severityConsequence,
    PAGE_LIGHT,
    PAGE_DARK,
} from '../../../lib/themeContrast';
import PreferenceInput from './PreferenceInput';
import ThemeFanOut from './ThemeFanOut';
import ThemePresetSelect from './ThemePresetSelect';

/**
 * The Theme Builder's control rail.
 *
 * WHAT CHANGED AND WHY
 * This shipped as a single scrolling column of cards, which was the right
 * shape for nine tokens and is not the right shape for 52: everything was
 * presented at one altitude, so nothing read as "start here" and the screen
 * produced analysis paralysis rather than a theme. The rebuild is a narrow
 * rail beside a permanent player preview (see `ThemePreview`), organised in
 * two tiers -- an Essentials block that opens with the screen, and Fine tune
 * accordions grouped by the SURFACE they paint.
 *
 * The tiering and the grouping both live in `THEME_GROUPS`, not here. This
 * component renders whatever tiers and groups it is handed, so promoting a
 * token out of Fine tune, or adding a surface, is a data edit.
 *
 * The preview is NOT owned here. It lives at the editor root
 * (`PlayerPreferenceEditor`) so it survives a tab switch and paints from
 * `merged` regardless of which tab is active.
 *
 * The inputs themselves still come from the metaMap through
 * `PreferenceInput`, exactly like every other section. Labels, descriptions
 * and input types stay in one place, and the parity test keeps them honest
 * against PMM. Only the arrangement is bespoke.
 */

/**
 * One control row: label above, control below, full rail width.
 *
 * The unset state is what this has to get right. Nineteen of the 52 tokens
 * fall back to ANOTHER settable token, so a blank field is not neutral -- it
 * paints a specific colour that moves with the rest of the theme. The control
 * is handed `resolvedDefault`, which walks that graph against the preference
 * being edited, rather than the static swatch table, which is only correct for
 * an unthemed preference.
 */
function ThemeControl (props) {
    const { propertyKey, merged, storeEdits, palette, findings } = props;
    const inert = inertWarning(propertyKey, merged);
    const variance = surfaceVariance(propertyKey, merged);
    const caution = sizeCaution(propertyKey, merged);
    const meta = getMeta(propertyKey);
    if (meta.hidden) return null;

    const isSet = Boolean(merged[propertyKey]);
    const clear = () => storeEdits(propertyKey, '');

    return (
        <div className="theme-row">
            <div className="theme-row__label">
                <span className="theme-row__name">{meta.displayName}</span>
                {/* Two levels, both of which used to sit unread on a `title`
                    attribute. The hint is a byline below; this carries the
                    full description, which is where the gotchas live.

                    A label cannot always carry its own meaning -- "Tab
                    Content Inset" is CSS vocabulary, and a viewer-facing
                    admin should not have to know what an inset is. Renaming
                    fixes the worst of those, but the mechanics (what the
                    shorthand accepts, which control it depends on) still need
                    somewhere to go, and it is not the docs: nobody opens
                    those until they are already stuck. */}
                {meta.description ? (
                    <InfoTip label={meta.displayName}>{meta.description}</InfoTip>
                ) : null}
                {/* Only a SET control offers a reset, and its presence is
                    also the "you changed this" mark -- one affordance doing
                    both jobs, rather than a dot plus a permanently-disabled
                    button on all 52 rows. */}
                {isSet ? (
                    <button
                        type="button"
                        className="theme-row__reset"
                        onClick={clear}
                        title="Use the player's default"
                        aria-label={'Reset ' + meta.displayName + " to the player's default"}
                    >
                        <i className="fa fa-undo" aria-hidden="true"/>
                    </button>
                ) : null}
            </div>
            {/* Always visible, one line. These are written as fragments
                rather than sentences ("Date, location, button labels"), which
                is what makes them affordable on every row -- and the rail's
                resting state is seven of them, not forty-eight, because the
                fine-tune groups are closed. */}
            {meta.hint ? <p className="theme-row__hint">{meta.hint}</p> : null}
            <div className="theme-row__control">
                <PreferenceInput
                    propertyKey={propertyKey}
                    meta={meta}
                    value={merged[propertyKey]}
                    onChange={storeEdits}
                    palette={palette}
                    fallback={resolvedDefault(propertyKey, merged)}
                />
            </div>
            {/* On the control that caused it. A contrast failure is only
                actionable next to the thing you would change, and the fix
                chip writes to THIS token rather than inferring a change
                somewhere else in the palette. */}
            {/* A control carrying a value that paints nothing. Quieter than a
                contrast warning -- nothing is WRONG, it just has no effect yet
                -- and only shown once the control has a value, since an empty
                one is simply unset. */}
            {inert ? (
                <p className="theme-row__inert">
                    <i className="fa fa-info-circle" aria-hidden="true"/>
                    {' ' + inert.message}
                </p>
            ) : null}
            {/* A value that works but costs something at the narrow end.
                Measured thresholds, shown only once reached -- see
                SIZE_CAUTIONS. Same grey as the inert notice on purpose: the
                admin is entitled to make this trade, so it informs rather
                than warns. */}
            {caution ? (
                <p className="theme-row__inert">
                    <i className="fa fa-info-circle" aria-hidden="true"/>
                    {' ' + caution.message}
                </p>
            ) : null}
            {/* A blank control whose default is not ONE colour.
                `revert-layer` rolls these back to the component's own utility,
                and the list surfaces do not share one -- so the single ghosted
                swatch above is right for the surface it names and wrong for
                the others. Showing the range is the only honest rendering of
                "default" here.

                Unset only: the moment the control carries a value, that value
                paints on every surface and the variance disappears. */}
            {variance ? (
                <div className="theme-row__variance">
                    <p className="theme-row__variance-headline">
                        <i className="fa fa-info-circle" aria-hidden="true"/>
                        {' ' + variance.note}
                    </p>
                    <ul className="theme-row__variance-list">
                        {variance.surfaces.map(surface => (
                            <li key={surface.label}>
                                <span
                                    className="theme-row__variance-swatch"
                                    style={{ backgroundColor: surface.value }}
                                    aria-hidden="true"
                                />
                                <span className="theme-row__variance-label">
                                    {surface.label}
                                    {/* Ties the note to the field above it. Without
                                        this the control reads as a contradiction:
                                        a swatch marked DEFAULT, and beneath it a
                                        note listing three different answers. */}
                                    {surface.shown ? (
                                        <span className="theme-row__variance-shown">
                                            {' (shown above)'}
                                        </span>
                                    ) : null}
                                </span>
                                <code className="theme-row__variance-value">{surface.value}</code>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {(findings || []).map((finding, i) => (
                <div className="theme-row__warning" key={i} role="alert">
                    <i className="fa fa-exclamation-triangle" aria-hidden="true"/>
                    <div className="theme-row__warning-body">
                        <p className="theme-row__warning-headline">{severityLabel(finding)}</p>

                        {/* The argument, made without vocabulary. This is the
                            failing colour on the real ground at the size the
                            player renders it -- so the control demonstrates
                            the problem instead of describing it. Nobody has to
                            know what a ratio is to read a sample and see that
                            they cannot. */}
                        <div
                            className="theme-row__warning-sample"
                            style={{ background: finding.ground }}
                            aria-hidden="true"
                        >
                            <span style={{ color: finding.ink }}>
                                {finding.where}
                            </span>
                        </div>

                        <p className="theme-row__warning-why">
                            {severityConsequence(finding)}
                            {finding.inherited
                                ? " This is the player's own default \u2014 setting a value here fixes it."
                                : ''}
                        </p>

                        <div className="theme-row__warning-foot">
                            {finding.fix ? (
                                <button
                                    type="button"
                                    className="theme-row__warning-fix"
                                    onClick={() => storeEdits(propertyKey, finding.fix)}
                                >
                                    <span
                                        className="theme-row__warning-swatch"
                                        aria-hidden="true"
                                        style={{ background: finding.fix }}
                                    />
                                    {'Use ' + finding.fix}
                                </button>
                            ) : null}
                            {/* Kept, demoted. Public bodies file conformance
                                reports and their auditors want the figure;
                                nobody else has to look at it. */}
                            <span
                                className="theme-row__warning-ratio"
                                title={'Measured against ' +
                                    (finding.againstPage
                                        ? 'a ' + (finding.pageLabel || 'light') + ' page'
                                        : 'its own background') +
                                    ' (' + finding.ground + ')'}
                            >
                                {finding.ratio + ':1 \u00b7 needs ' + finding.required}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

/** How many keys in a set carry a value -- the count on a group header. */
function setCount (keys, merged) {
    return keys.filter(key => merged[key]).length;
}

export default function ThemeTab (props) {
    const {
        merged,
        storeEdits,
        prefID,
        isDirty,
        actions,
        onWorkspaceAction,
        darkPage,
    } = props;

    const section = sections.find(s => s.key === 'theme');
    const keys = getPropertyKeysBySection('theme');
    const theme = extractTheme(merged);
    const themed = hasTheme(merged);
    // Derived from the values, so it cannot claim a look they no longer
    // have. Null means hand-tuned -- the selector says "Custom".
    const activePreset = matchPreset(merged);

    // Some tokens are inert without a partner. Choosing an outline COLOUR
    // paints nothing, because the shared width token defaults to 0 -- so the
    // editor writes the width too, which is what this repo's own control
    // descriptions, the player's CSS comment and PMM's schema comment have all
    // claimed it does since round 2. See `pairTabOutline` for why this pair is
    // automated and the other two pairing gaps are deliberately not.
    //
    // Deliberately NOT on the preset path below: a preset writes a complete,
    // self-consistent bag including the width, and pairing on top of that
    // would second-guess a look that was authored as a whole.
    const storeThemeEdit = useCallback((key, value) => {
        storeEdits(key, value);
        const paired = pairTabOutline(key, value, merged);
        if (paired) {
            Object.keys(paired).forEach(k => storeEdits(k, paired[k]));
        }
    }, [storeEdits, merged]);

    const applyPreset = useCallback((preset) => {
        const values = presetValues(preset);
        Object.keys(values).forEach(key => storeEdits(key, values[key]));
    }, [storeEdits]);

    // Fine-tune groups are CLOSED by default -- that is the whole point of
    // the tier. Keyed by group key; absent means closed.
    const [openGroups, setOpenGroups] = useState({});
    // Finding a control among 52 by knowing which group we filed it under is a
    // guess the user should not have to make. Searching also rescues the case
    // where they have opened every accordion and are back to one long list.
    const [query, setQuery] = useState('');
    // Undefined means "follow whether the scope carries values"; an explicit
    // true/false means the user has decided for this session.
    const [scopedOpenState, setScopedOpenState] = useState({});
    const toggleScoped = useCallback((key, next) => {
        setScopedOpenState(prev => ({ ...prev, [key]: next }));
    }, []);
    const toggleGroup = useCallback((key) => {
        setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // Build the groups from THEME_GROUPS, but only over keys the section
    // actually exposes, then append any section key no group claimed as a
    // final unlabelled group -- so a future PMM token is shown, not dropped.
    // Matches label, hint, description AND the token key -- the last so
    // support can paste a key from a preference blob and land on its control.
    const needle = query.trim().toLowerCase();
    const matches = (key) => {
        if (!needle) return true;
        const meta = getMeta(key);
        if (!meta) return false;
        return [key, meta.displayName, meta.hint, meta.description]
            .some(field => field && String(field).toLowerCase().indexOf(needle) !== -1);
    };

    const grouped = THEME_GROUPS
        .map(group => ({
            ...group,
            keys: group.keys.filter(key => keys.indexOf(key) !== -1 && matches(key)),
            subGroups: group.subGroups
                ? group.subGroups
                    .map(sub => ({
                        ...sub,
                        keys: sub.keys.filter(key => keys.indexOf(key) !== -1 && matches(key)),
                    }))
                    .filter(sub => sub.keys.length > 0)
                : null,
        }))
        .filter(group => group.keys.length > 0);

    const claimed = new Set(grouped.reduce((all, g) => all.concat(g.keys), []));
    const leftover = keys.filter(key => !claimed.has(key) && matches(key));

    // A group carrying `scopeOf` is a NARROWED VARIANT of another group --
    // the same levers aimed at one surface -- not a section in its own
    // right. Rendered as a peer it reads as an independent set of settings
    // and invites someone to fill it in when the group above already covers
    // them. So it lives inside its parent, behind a disclosure.
    const scopedByParent = grouped.reduce((acc, g) => {
        if (!g.scopeOf) return acc;
        (acc[g.scopeOf] = acc[g.scopeOf] || []).push(g);
        return acc;
    }, {});

    const essentials = grouped.find(g => g.tier === 'essential');
    const fineTune = grouped.filter(g => g.tier !== 'essential' && !g.scopeOf);
    const renderGroups = leftover.length
        ? fineTune.concat([{ key: '_other', title: 'Not yet grouped', keys: leftover }])
        : fineTune;

    // Contrast ownership lives here, not in the player: when a client opts
    // into a background its ink cannot be read against -- a themed dialog
    // card, a filled tab pill -- the group says so, and offers a one-click
    // fix rather than only flagging it. Non-blocking: the un-themed defaults
    // are legible, and a suggestion never auto-applies; it derives the fix
    // and waits for the click. Keyed by group so each callout lands in the
    // group whose controls caused it.
    const suggestions = {
        surface: modalSuggestion(merged),
        tabs: tabSuggestion(merged),
    };

    // The general contrast audit, measured against whatever the preview says
    // is behind the player. The two suggestions above stay as they are: they
    // carry fix logic the audit does not (the modal ink ramp, and the tab
    // strip case that deliberately offers no fix), so this reports alongside
    // them rather than replacing them.
    const page = darkPage ? PAGE_DARK : PAGE_LIGHT;
    const pageLabel = darkPage ? 'dark' : 'light';
    const audit = auditContrast(merged, page).map(f => ({ ...f, pageLabel }));
    const tintWarning = tintDirectionWarning(merged, page);
    const allFindings = tintWarning
        ? audit.concat([{ ...tintWarning, pageLabel }])
        : audit;
    const byKey = findingsByKey(allFindings);
    const applyFix = useCallback((fix) => {
        if (!fix || !fix.values) return;
        Object.keys(fix.values).forEach(key => storeEdits(key, fix.values[key]));
    }, [storeEdits]);

    // Saving is NOT a prerequisite. Fan-out applies the theme on screen,
    // and a never-saved preference has no prefID precisely because it is
    // not in the client's list yet -- so there is nothing to exclude and
    // nothing to wait for. What remains are the two cases where the
    // action genuinely cannot do anything: no theme to copy, and preview
    // builds, where writes to existing preferences are blocked anyway.
    const fanOutDisabled = !themed;
    const fanOutReason = 'Set a theme above and this will copy it to your other player preferences.';

    // The colours this theme already uses, offered as swatches in every
    // picker. Deduped and capped: it is a shortcut back to a colour you have
    // already chosen, not an inventory -- past a row or two it stops being
    // scannable and becomes another grid to search.
    const palette = THEME_KEYS
        .filter(key => getMeta(key) && getMeta(key).inputType === 'color')
        .map(key => merged[key])
        .filter(v => v && v !== 'transparent')
        .filter((v, i, all) => all.indexOf(v) === i)
        .slice(0, 12);

    const renderRows = (groupKeys) => groupKeys.map(key => (
        <ThemeControl
            key={key}
            propertyKey={key}
            merged={merged}
            storeEdits={storeThemeEdit}
            palette={palette}
            findings={byKey[key]}
        />
    ));

    const renderScoped = (groupKey) => (scopedByParent[groupKey] || []).map(scoped => {
        /* Open when it already carries values, so an existing override is
           never hidden behind a collapsed heading; closed otherwise, so the
           default reading is "these lists all match". */
        const hasValues = scoped.keys.some(k => merged[k]);
        const scopedOpen = scopedOpenState[scoped.key] !== undefined
            ? scopedOpenState[scoped.key]
            : hasValues;
        return (
            <div className="theme-scoped" key={scoped.key}>
                <button
                    type="button"
                    className="theme-scoped__toggle"
                    aria-expanded={scopedOpen}
                    onClick={() => toggleScoped(scoped.key, !scopedOpen)}
                >
                    <i
                        className={'fa theme-rail__chevron ' +
                            (scopedOpen ? 'fa-chevron-down' : 'fa-chevron-right')}
                        aria-hidden="true"
                    />
                    <span>{'Style the ' + (scoped.scopeLabel || scoped.title).toLowerCase() + ' differently'}</span>
                </button>
                {scopedOpen ? (
                    <div className="theme-scoped__rows">{renderRows(scoped.keys)}</div>
                ) : null}
            </div>
        );
    });

    /* A callout always states the problem; the apply button appears only when
       the fix is one the Builder can safely make on a click (see
       tabSuggestion's strip case, which is deliberately message-only). */
    const renderSuggestion = (fix) => (fix ? (
        <div className="theme-suggestion" role="alert">
            <i className="fa fa-exclamation-triangle theme-suggestion__icon" aria-hidden="true"/>
            <div className="theme-suggestion__body">
                <p className="theme-suggestion__message">{fix.message}</p>
                {fix.values ? (
                    <button
                        type="button"
                        className="theme-suggestion__apply"
                        onClick={() => applyFix(fix)}
                    >
                        <span
                            className="theme-suggestion__swatches"
                            aria-hidden="true"
                            style={{ background: fix.preview }}
                        >
                            {fix.swatches.map((hex, i) => (
                                <i key={i} style={{ background: hex }}/>
                            ))}
                        </span>
                        {fix.label}
                    </button>
                ) : null}
            </div>
        </div>
    ) : null);

    const fineTuneSet = renderGroups.reduce((n, g) => n + setCount(g.keys, merged), 0) +
        Object.keys(scopedByParent).reduce((n, parent) => (
            n + scopedByParent[parent].reduce((m, s) => m + setCount(s.keys, merged), 0)
        ), 0);

    return (
        <div id="tab" className="theme-tab theme-rail">
            <header className="theme-rail__head">
                <div className="theme-tab__intro">
                    <h2>{section ? section.displayName : 'Theme Builder'}</h2>
                </div>
                <ThemePresetSelect current={activePreset} onApply={applyPreset}/>

                <div className="theme-rail__search">
                    <i className="fa fa-search" aria-hidden="true"/>
                    <input
                        type="search"
                        className="form-control"
                        value={query}
                        placeholder="Search settings"
                        aria-label="Search theme settings"
                        onChange={ev => setQuery(ev.target.value)}
                    />
                    {query ? (
                        <button
                            type="button"
                            className="theme-rail__search-clear"
                            onClick={() => setQuery('')}
                            aria-label="Clear search"
                            title="Clear search"
                        >
                            <i className="fa fa-times" aria-hidden="true"/>
                        </button>
                    ) : null}
                </div>
            </header>

            <div className="theme-rail__scroll">

                {/* ESSENTIALS -- no accordion, no header chevron. The block
                    that opens with the screen is the answer to "where do I
                    start", and a collapsible one can be closed into the same
                    wall of headings the rebuild exists to remove. */}
                {essentials && essentials.keys.length ? (
                    <section className="theme-rail__essentials">
                        <h3 className="theme-rail__tier-title">{essentials.title}</h3>
                        <div className="theme-rail__rows">{renderRows(essentials.keys)}</div>
                    </section>
                ) : null}

                <div className="theme-rail__tier-break">
                    <h3 className="theme-rail__tier-title">Fine tune</h3>
                    {/* The count is the only thing carrying state across the
                        collapsed groups, so it has to be here: without it a
                        closed rail cannot tell you whether you have set
                        anything below the fold. */}
                    {fineTuneSet ? (
                        <span className="theme-rail__tier-count">{fineTuneSet} set</span>
                    ) : null}
                </div>

                {renderGroups.map((group) => {
                    // While searching, a group with matches is open: a result
                    // hidden behind a collapsed heading is not a result. Same
                    // rule the scoped disclosure follows for values.
                    const isOpen = needle ? true : !!openGroups[group.key];
                    const fix = suggestions[group.key];
                    const scoped = scopedByParent[group.key] || [];
                    const count = setCount(group.keys, merged) +
                        scoped.reduce((n, s) => n + setCount(s.keys, merged), 0);
                    // Contrast failures hidden inside a closed group, counted
                    // out on the header. A warning nobody can see is not a
                    // warning.
                    const groupKeys = group.keys.concat(
                        scoped.reduce((all, sc) => all.concat(sc.keys), [])
                    );
                    const warnCount = groupKeys
                        .reduce((n, k) => n + ((byKey[k] || []).length), 0);
                    return (
                        <section
                            key={group.key}
                            className={'theme-group' + (isOpen ? ' theme-group--open' : '')}
                        >
                            <button
                                type="button"
                                className="theme-group__toggle"
                                aria-expanded={isOpen}
                                onClick={() => toggleGroup(group.key)}
                            >
                                <i
                                    className={'fa theme-rail__chevron ' +
                                        (isOpen ? 'fa-chevron-down' : 'fa-chevron-right')}
                                    aria-hidden="true"
                                />
                                <span className="theme-group__title">{group.title}</span>
                                {/* A contrast problem inside a closed group is
                                    invisible, and these are exactly the states
                                    a user cannot diagnose from the preview --
                                    they can see it looks wrong, not which
                                    control caused it. So the header carries
                                    the warning out. */}
                                {fix || warnCount ? (
                                    <i
                                        className="fa fa-exclamation-triangle theme-group__warning"
                                        title={fix ? fix.message : (
                                            warnCount + (warnCount === 1
                                                ? ' contrast issue in this group'
                                                : ' contrast issues in this group'))}
                                        // A bare <i> has no role, so an
                                        // aria-label on it is dropped -- the
                                        // warning was invisible to a screen
                                        // reader while looking labelled.
                                        role="img"
                                        aria-label={fix ? fix.message : (warnCount + ' contrast issues')}
                                    />
                                ) : null}
                                {count ? <span className="theme-group__count">{count}</span> : null}
                            </button>

                            {isOpen ? (
                                <div className="theme-group__body">
                                    {group.subGroups ? group.subGroups.map(sub => (
                                        /* Sub-sections are presentation only:
                                           one surface split into its parts, so
                                           "the tabs" is one place rather than
                                           four peers. No disclosure and no
                                           open-when-set rule -- that belongs
                                           to scoped groups, which mean
                                           something different. */
                                        <div className="theme-subgroup" key={sub.key}>
                                            <h4 className="theme-subgroup__title">{sub.title}</h4>
                                            <div className="theme-rail__rows">{renderRows(sub.keys)}</div>
                                        </div>
                                    )) : (
                                        <div className="theme-rail__rows">{renderRows(group.keys)}</div>
                                    )}
                                    {renderScoped(group.key)}
                                    {renderSuggestion(fix)}
                                </div>
                            ) : null}
                        </section>
                    );
                })}

                {needle && !essentials && !renderGroups.length ? (
                    <p className="theme-rail__empty">
                        {'Nothing matches \u201c' + query + '\u201d.'}
                    </p>
                ) : null}

                <ThemeFanOut
                    theme={theme}
                    prefID={prefID}
                    isDirty={isDirty}
                    disabled={fanOutDisabled}
                    disabledReason={fanOutReason}
                    actions={actions}
                    onWorkspaceAction={onWorkspaceAction}
                />
            </div>
        </div>
    );
}
